/* eslint-disable no-restricted-syntax, consistent-return */
import { useParams, useSearchParams } from 'react-router-dom';
import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import useTheme from 'chat-ui/src/hooks/useTheme';
import { SearchParam } from '@common/ts';
import { ChevronDown, Check, Loader2, Search } from 'lucide-react';
import useTranslation from '@/hooks/useTranslation';
import { useBottomAwareStickyTop } from '@/hooks';
import FetchErrorState from '@/components/ui/FetchErrorState';
import QuestionItem from '@/components/QuestionItem/QuestionItem';
import QuestionSidebar from '@/components/QuestionItem/QuestionSidebar';
import QuestionStatsBar from '@/components/QuestionItem/QuestionStatsBar';
import useReportStore from '@/store/ReportStore';
import FullPageLoader from '@/components/atoms/Loader/FullPageLoader';
import NavigationControls from '@/components/NavigationControls/NavigationControls';
import { cn } from '@/lib/utils';
import {
  useGetInterviewQuestions,
  useGetProjectQuestionInsights,
  useGetQuestionCategories,
} from '@/reactQuery/get';
import useCategoryStore from '@/store/CategoryStore';
import { useCurrentProject } from '@/hooks/useCurrentProject';
import { ConversationState } from '@/enums';
import { InterviewQuestion } from '@/types';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import SectionNav from '@/components/analysis/SectionNav';

interface QuestionsProps {
  isPendingReRun: boolean;
  analysisLanguage: string;
  newConversationsCount: number;
  onReRun: () => void;
  isRerunLoading: boolean;
  newConversationsState: ConversationState;
  isLoadingBeforeApplyFilter: boolean;
  setSelectedQuestionId: (questionId: string) => void;
  selectedQuestionId: string;
}

function Questions({
  isPendingReRun,
  analysisLanguage,
  newConversationsCount,
  onReRun,
  isRerunLoading,
  newConversationsState,
  isLoadingBeforeApplyFilter,
  setSelectedQuestionId,
  selectedQuestionId,
}: QuestionsProps) {
  const { lang } = useTranslation();
  const { project_id: projectId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const filterId = searchParams.get(SearchParam.FILTER_ID);
  const questionIdFromUrl = searchParams.get(SearchParam.QUESTION_ID);
  const { currentProject: singleProjectData } = useCurrentProject();
  const { currentTheme } = useTheme();

  const [selectedQuestionText, setSelectedQuestionText] = useState<string>('');
  const setIsQuestionDataSelected = useReportStore((state) => state.setIsQuestionDataSelected);

  const { data: interviewQuestions, isPending: isPendingInterviewQuestions } =
    useGetInterviewQuestions(filterId, projectId);

  const {
    data: selectedQuestionInsights,
    isPending: isPendingInsights,
    isFetching: isFetchingInsights,
    error: errorInsights,
    refetch: refetchInsights,
  } = useGetProjectQuestionInsights(filterId, projectId, selectedQuestionId);

  const categoryType = useCategoryStore((state) => state.categoryType);
  const { data: categoryData } = useGetQuestionCategories(filterId, projectId, selectedQuestionId);

  const selectedQuestion = interviewQuestions?.find((item) => item.id === selectedQuestionId);
  const selectedQuestionMedia = selectedQuestion?.media || [];

  // Pick the active category set based on store mode
  const activeCategoryList =
    categoryType === 'multiple' ? categoryData?.categories_multi : categoryData?.categories;

  const onQuestionChange = (questionId: string) => {
    const question = interviewQuestions?.find((item) => item.id === questionId);
    if (!question) return;

    setSelectedQuestionId(questionId);
    setSelectedQuestionText(question?.question);
    setIsQuestionDataSelected(false);
    // Persist the selection in the URL so the tab unmount/remount on "Go Back"
    // (and refresh / deep-link) restores the same question + its graph.
    // TELV1-322.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(SearchParam.QUESTION_ID, questionId);
        return next;
      },
      { replace: true },
    );
  };

  // Initialise / re-initialise the selected question whenever the question list
  // (re)loads. A valid ?question_id from the URL wins (it survives the Questions
  // tab unmount/remount triggered by opening a transcript and clicking "Go Back",
  // plus refresh / deep-link — TELV1-322); otherwise fall back to the first
  // question. A stale/invalid id also degrades to the first question.
  useEffect(() => {
    if (!interviewQuestions?.length) return;
    const target =
      interviewQuestions.find((q) => q.id === questionIdFromUrl) ?? interviewQuestions[0];
    setSelectedQuestionId(target.id);
    setSelectedQuestionText(target.question);
  }, [interviewQuestions, questionIdFromUrl]);

  const currentIndex = interviewQuestions?.findIndex((q) => q.id === selectedQuestionId) ?? 0;
  const totalQuestions = interviewQuestions?.length ?? 0;
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < totalQuestions - 1;

  const goToPrev = () => {
    if (hasPrev && interviewQuestions) onQuestionChange(interviewQuestions[currentIndex - 1].id);
  };
  const goToNext = () => {
    if (hasNext && interviewQuestions) onQuestionChange(interviewQuestions[currentIndex + 1].id);
  };

  // Bottom-aware sticky offset so a sidebar taller than the viewport can
  // still be scrolled through (TELV1-542) — see useBottomAwareStickyTop.
  const { ref: sidebarRef, top: sidebarStickyTop } = useBottomAwareStickyTop();

  // Combobox state
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setHighlightedIndex(0);
    }, 150);
  }, []);

  const filteredQuestions =
    interviewQuestions?.filter((item: InterviewQuestion) =>
      item.question.toLowerCase().includes(debouncedSearch.toLowerCase()),
    ) ?? [];

  const handleOpenChange = (open: boolean) => {
    setPopoverOpen(open);
    if (!open) {
      setSearchQuery('');
      setDebouncedSearch('');
      setHighlightedIndex(0);
    }
  };

  // Auto-focus search input when popover opens
  useEffect(() => {
    if (popoverOpen) {
      // Small delay to let the popover render
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [popoverOpen]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (!listRef.current) return;
    const highlighted = listRef.current.querySelector<HTMLElement>('[data-highlighted="true"]');
    highlighted?.scrollIntoView({ block: 'nearest' });
  }, [highlightedIndex]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.min(prev + 1, filteredQuestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = filteredQuestions[highlightedIndex];
      if (item) {
        onQuestionChange(item.id);
        handleOpenChange(false);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleOpenChange(false);
    }
  };

  // Section nav for the per-question zones.
  // TELV1-296 UX commit 6 — Categories moved up to be the second
  // section (right below Summary) per QA feedback, with the bottom slot
  // now holding the legacy Themes & Opportunities block (qsection-themes).
  // TELV1-296 UX commit 7 — the Themes & Opportunities entry is hidden
  // when there's no legacy data, mirroring the conditional render of
  // `qsection-themes` in QuestionItem.tsx so the nav doesn't dangle a
  // dead-link button for a section that isn't in the DOM.
  const hasLegacyThemes =
    !selectedQuestionInsights?.patterns_and_trends?.length &&
    (selectedQuestionInsights?.themes?.length || 0) +
      (selectedQuestionInsights?.opportunities?.length || 0) >
      0;
  const questionSections = useMemo(
    () => [
      { id: 'qsection-summary', label: lang.get('msg.summary') },
      { id: 'qsection-categories', label: lang.get('msg.categories') },
      { id: 'qsection-answers', label: lang.get('msg.respondentAnswers') || 'Answers' },
      { id: 'qsection-evidence', label: lang.get('msg.keyFindings') || 'Evidence' },
      ...(hasLegacyThemes
        ? [{ id: 'qsection-themes', label: lang.get('msg.themesAndOpportunities') || 'Themes' }]
        : []),
    ],
    [lang, hasLegacyThemes],
  );

  const [activeQSectionId, setActiveQSectionId] = useState('qsection-summary');
  const visibleQSectionsRef = useRef(new Set<string>());
  useEffect(() => {
    const ids = questionSections.map((s) => s.id);
    const elements = ids.map((id) => document.getElementById(id)).filter(Boolean) as HTMLElement[];
    if (elements.length === 0) return undefined;
    visibleQSectionsRef.current.clear();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            visibleQSectionsRef.current.add(entry.target.id);
          } else {
            visibleQSectionsRef.current.delete(entry.target.id);
          }
        }
        // Pick the LAST visible section in document order (i.e. the
        // section nearest the top of the viewport's active band).
        // Picking the first-in-document would keep a tall first section
        // highlighted forever — see Overview.tsx for the same fix.
        const lastVisible = [...ids].reverse().find((id) => visibleQSectionsRef.current.has(id));
        if (lastVisible) {
          setActiveQSectionId(lastVisible);
        }
      },
      { rootMargin: '-130px 0px -40% 0px', threshold: 0 },
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [questionSections, selectedQuestionId]);

  // The error was detected here and then thrown away into a blank white
  // page (`return null`) — worse than no handling at all (TELV1-544).
  if (errorInsights) {
    // While a Retry is in flight, error stays set — show the loader so the
    // click visibly does something instead of a frozen error (PR review).
    if (isFetchingInsights) return <FullPageLoader />;
    return <FetchErrorState className="p-40" onRetry={() => refetchInsights()} />;
  }
  if (isPendingInsights || isLoadingBeforeApplyFilter) return <FullPageLoader />;

  return (
    <div className="w-full">
      {/* Question header with navigation — full width above the grid */}
      <div className="mb-5">
        {/* Question text + stepper in one row */}
        <div className="flex items-start gap-4">
          {/* Question title — clickable to open combobox */}
          <div className="relative flex-1 min-w-0">
            <Popover open={popoverOpen} onOpenChange={handleOpenChange}>
              <PopoverTrigger asChild>
                <button type="button" className="flex items-start gap-2 w-full text-left group">
                  <h2 className="text-xl font-semibold text-neutral-900 leading-snug group-hover:text-neutral-700 transition-colors line-clamp-3">
                    {isPendingInterviewQuestions ? (
                      <Loader2
                        style={{ color: currentTheme?.primary }}
                        className="h-5 w-5 animate-spin"
                      />
                    ) : (
                      // Prefix with the question number to match the
                      // popover items (e.g. "3. How do you feel..."),
                      // so the selected state isn't missing context the
                      // dropdown list already provides.
                      <>
                        {selectedQuestionId && interviewQuestions && (
                          <span className="text-neutral-400 font-semibold mr-2">
                            {currentIndex + 1}.
                          </span>
                        )}
                        {selectedQuestionText}
                      </>
                    )}
                  </h2>
                  <ChevronDown
                    className={cn(
                      'h-5 w-5 text-neutral-400 shrink-0 mt-1 transition-transform',
                      popoverOpen && 'rotate-180',
                    )}
                  />
                </button>
              </PopoverTrigger>

              <PopoverContent
                align="start"
                sideOffset={8}
                className="p-0 rounded-xl border border-neutral-200 shadow-lg w-[var(--radix-popover-trigger-width)] max-w-none"
                onOpenAutoFocus={(e) => e.preventDefault()}
              >
                {/* Search input */}
                <div className="flex items-center gap-2 px-4 py-2.5 border-b border-neutral-100">
                  <Search className="h-4 w-4 text-neutral-400 shrink-0" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Search questions…"
                    className="flex-1 text-sm text-neutral-700 placeholder:text-neutral-400 bg-transparent outline-none"
                  />
                </div>

                {/* Question list */}
                <div ref={listRef} className="max-h-[360px] overflow-y-auto">
                  {filteredQuestions.length === 0 ? (
                    <p className="px-4 py-6 text-sm text-neutral-400 text-center">
                      No matching questions
                    </p>
                  ) : (
                    filteredQuestions.map((item: InterviewQuestion, index: number) => {
                      // Resolve the original index for the number badge
                      const originalIndex =
                        interviewQuestions?.findIndex((q) => q.id === item.id) ?? index;
                      const isSelected = item.id === selectedQuestionId;
                      const isHighlighted = index === highlightedIndex;

                      return (
                        <button
                          key={item.id}
                          type="button"
                          data-highlighted={isHighlighted}
                          onClick={() => {
                            onQuestionChange(item.id);
                            handleOpenChange(false);
                          }}
                          onMouseEnter={() => setHighlightedIndex(index)}
                          className={cn(
                            'flex items-start gap-3 w-full text-left px-4 py-3 transition-colors border-b border-neutral-100 last:border-b-0',
                            isSelected && 'bg-emerald-50/50',
                            isHighlighted && !isSelected && 'bg-neutral-50',
                          )}
                        >
                          <span className="text-sm font-semibold text-neutral-400 shrink-0 mt-0.5 w-6">
                            {originalIndex + 1}
                          </span>
                          <span className="text-sm text-neutral-700 leading-snug flex-1">
                            {item.question}
                          </span>
                          {isSelected && (
                            <Check className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                          )}
                        </button>
                      );
                    })
                  )}
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* Stepper — right side */}
          <div className="shrink-0 pt-0.5">
            <NavigationControls
              onPrev={goToPrev}
              onNext={goToNext}
              isPrevDisabled={!hasPrev}
              isNextDisabled={!hasNext}
              label={`${lang.get('msg.question')} ${currentIndex + 1} / ${totalQuestions}`}
            />
          </div>
        </div>
      </div>

      {/* 2-column grid: main content + sticky sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5">
        {/* Main column */}
        <div className="min-w-0">
          {selectedQuestionId && (
            <QuestionStatsBar
              questionId={selectedQuestionId}
              responseCount={categoryData?.conversations_num}
              patternCount={selectedQuestionInsights?.patterns_and_trends?.length}
              divergentCount={selectedQuestionInsights?.divergent_views_and_outliers?.length}
              quoteCount={selectedQuestionInsights?.voice_quotes?.length}
            />
          )}
          <QuestionItem
            isPendingReRun={isPendingReRun}
            selectedQuestionInsights={selectedQuestionInsights}
            questionID={selectedQuestionId}
            questionText={selectedQuestionText}
            analysisLanguage={analysisLanguage}
            singleProjectData={singleProjectData}
            newConversationsCount={newConversationsCount}
            onReRun={onReRun}
            isRerunLoading={isRerunLoading}
            newConversationsState={newConversationsState}
            projectId={projectId}
            isPendingInterviewQuestions={isPendingInterviewQuestions}
            questionStimuli={selectedQuestionMedia}
          />
        </div>

        {/* Sidebar — hidden on small screens, sticky on large. The
            sticky `top` is measured (useBottomAwareStickyTop): when the
            sidebar outgrows the viewport it anchors negatively so the
            page scrolls through the whole sidebar before it sticks —
            same pattern and reasoning as Overview.tsx (TELV1-542; the
            previous comment here claimed a max-h cap that never
            existed). */}
        {selectedQuestionId && (
          <div
            ref={sidebarRef}
            style={{ top: sidebarStickyTop }}
            className="hidden lg:flex flex-col gap-4 sticky self-start"
          >
            <QuestionSidebar
              questionId={selectedQuestionId}
              responseCount={categoryData?.conversations_num}
              categories={activeCategoryList}
              patternCount={selectedQuestionInsights?.patterns_and_trends?.length}
              divergentCount={selectedQuestionInsights?.divergent_views_and_outliers?.length}
              quoteCount={selectedQuestionInsights?.voice_quotes?.length}
            />
            <SectionNav
              sections={questionSections}
              activeId={activeQSectionId}
              analysisLanguage={analysisLanguage}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default Questions;
